import { useEffect } from 'react';
import { useStore } from '../../store/useStore';
import { supabase } from '../../config/supabase';
import toast from 'react-hot-toast';
import { logger } from '../../utils/logger';

export const useAuth = () => {
  const { setUser, setLoading, setError, fetchEmployees } = useStore();

  useEffect(() => {
    let mounted = true;

    const initAuth = async () => {
      try {
        setLoading(true);

        // Get initial session
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session?.user) {
          // Get user profile
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', session.user.id)
            .single();

          if (profileError) throw profileError;

          if (mounted && profile) {
            setUser({
              id: profile.id,
              email: profile.email,
              name: profile.name,
              role: profile.role,
              department: profile.department,
              avatar: profile.avatar || `https://api.dicebear.com/7.x/avatars/svg?seed=${profile.email}`,
              phone: profile.phone
            });

            // Fetch all employees after successful auth
            await fetchEmployees();
          }
        }

        // Listen for auth changes
        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
          if (event === 'SIGNED_IN' && session?.user) {
            const { data: profile, error: profileError } = await supabase
              .from('profiles')
              .select('*')
              .eq('id', session.user.id)
              .single();

            if (profileError) {
              logger.error('Profile fetch error:', profileError);
              return;
            }

            if (mounted && profile) {
              setUser({
                id: profile.id,
                email: profile.email,
                name: profile.name,
                role: profile.role,
                department: profile.department,
                avatar: profile.avatar || `https://api.dicebear.com/7.x/avatars/svg?seed=${profile.email}`,
                phone: profile.phone
              });

              // Fetch all employees after sign in
              await fetchEmployees();
              
              toast.success('Signed in successfully');
            }
          } else if (event === 'SIGNED_OUT') {
            if (mounted) {
              setUser(null);
              toast.success('Signed out successfully');
            }
          }
        });

        return () => {
          mounted = false;
          subscription.unsubscribe();
        };
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Authentication error';
        if (mounted) {
          logger.error('Auth initialization error:', error);
          setError(message);
          toast.error('Failed to initialize authentication');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initAuth();
    
    return () => {
      mounted = false;
    };
  }, [setUser, setLoading, setError, fetchEmployees]);
};